import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, subirArchivo } from "@/lib/api";
import { useExpediente } from "@/context/ExpedienteContext";
import { Spinner } from "@/components/common";
import { LockBanner } from "@/components/portalCommon";
import { DOC_ESTADO } from "@/constants/labels";
import { UploadCloud, FileText, CheckCircle2, Eye } from "lucide-react";
import DocViewer from "@/components/DocViewer";
import { MotivosCorreccion, VolverACargarBtn } from "@/components/RepetirCarga";

export default function Documentos() {
  const { exp, meta, reload } = useExpediente();
  const [categoria, setCategoria] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const inputRef = useRef();

  if (!exp || !meta) return <div className="flex h-64 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;
  const locked = exp.bloqueado;
  const docs = (exp.documentos || []).filter((d) => !d.beneficiario_id);

  const subir = async () => {
    if (!categoria || !file) { toast.error("Selecciona categoría y archivo"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("categoria", categoria); fd.append("file", file);
      await subirArchivo("/portal/documentos", fd);
      toast.success("Documento cargado (Recibido)");
      setFile(null); setCategoria(""); reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const confirmar = async (id) => {
    try { await api.patch(`/portal/documentos/${id}/confirmar`); toast.success("Documento confirmado"); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Mis documentos</h1>
        <p className="mt-1 text-sm text-slate-500">Carga y consulta tus documentos. PDF, JPG o PNG (máx. 10 MB).</p>
      </div>
      {locked && <LockBanner />}

      {!locked && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <select value={categoria} onChange={(e) => setCategoria(e.target.value)} data-testid="doc-categoria" className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-vino-500">
                <option value="">Categoría del documento</option>
                {meta.categorias_documento.map((c) => <option key={c}>{c}</option>)}
              </select>
              <button onClick={() => inputRef.current?.click()} className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 hover:border-vino-400 hover:bg-vino-50/40" data-testid="doc-seleccionar">
                <UploadCloud className="h-4 w-4" /> {file ? file.name : "Seleccionar archivo"}
              </button>
              <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="doc-input" onChange={(e) => setFile(e.target.files[0])} />
            </div>
            <button onClick={subir} disabled={uploading} data-testid="doc-subir" className="flex items-center justify-center gap-2 rounded-lg bg-vino-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">
              {uploading ? <Spinner /> : <UploadCloud className="h-4 w-4" />} Subir
            </button>
          </div>
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">Subir un archivo no significa que esté validado. HUMAN lo revisará.</p>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {docs.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-400">Aún no has cargado documentos.</p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="doc-lista">
            {docs.map((d) => {
              const est = DOC_ESTADO[d.estado] || DOC_ESTADO.recibido;
              return (
                <div key={d.id} className="flex flex-wrap items-center gap-3 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-vino-50 text-vino-700"><FileText className="h-5 w-5" /></div>
                  <div className="min-w-0 flex-1 basis-40">
                    <p className="break-words text-sm font-semibold text-slate-800">{d.categoria}</p>
                    <p className="truncate text-xs text-slate-400">{d.original_filename} · {(d.size / 1024).toFixed(0)} KB</p>{d.protegido && <span className="mt-1 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`badge-protegido-${d.id}`}>PDF protegido con contraseña</span>}
                    {d.cargado_por_tipo === "human" && <p className="text-[11px] font-medium text-vino-700" data-testid={`doc-cargado-human-${d.id}`}>Cargado por HUMAN</p>}
                  </div>
                  <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${est.cls}`}>{est.label}</span>
                  <button onClick={() => setViewDoc(d)} data-testid={`doc-ver-${d.id}`} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-vino-700 hover:bg-vino-50">
                    <Eye className="h-3.5 w-3.5" /> Ver
                  </button>
                  {!locked && d.estado === "recibido" && (
                    <button onClick={() => confirmar(d.id)} data-testid={`doc-confirmar-${d.id}`} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-vino-700 hover:bg-vino-50">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Confirmar
                    </button>
                  )}
                  {d.estado === "correccion_requerida" && <VolverACargarBtn doc={d} onDone={reload} />}
                  {d.estado === "correccion_requerida" && <MotivosCorreccion c={d.correccion} />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {viewDoc && (
        <DocViewer
          url={`/portal/documentos/${viewDoc.id}/download`}
          titulo={viewDoc.categoria}
          subtitulo={viewDoc.original_filename}
          nombreArchivo={viewDoc.original_filename}
          onClose={() => setViewDoc(null)}
        />
      )}
    </div>
  );
}
